<template>
  <div
    v-if="entries.length > 0"
    class="square-badge__container"
  >
    <template v-for="(entry, key) in entries">
      <SquareBadeCl
        :key="key"
        v-bind="entry"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, withDefaults } from 'vue';
import { storeToRefs } from 'pinia';
import { useOfferStore } from '~/stores/offers';
import { getDiscount, getPrice } from '@i22/rocket';
import { SquareBadge as SquareBadeCl } from '@i22/td-longtail-vue-components';

import type { Price } from '~/lib/goliath/goliath.types';
import type { Offer } from '~/lib/goliath/offer-by-slugs';

import type { SquareBadgeComponentProps } from '@i22/td-longtail-vue-components/dist/components/square-badge.vue';

type Badge = {
  text: string;
  footnote?: string;
  className?: string;
  color?: string;
  style?: any;
};

export type SquareBadgeProps = {
  offer?: Offer;
  prices?: Price[];
  text?: string;
  footnoteText?: string;
  customBadges?: Badge[];
  discountText?: string;
  className?: string;
  showOnlyOne?: boolean;
};

const props = withDefaults(defineProps<SquareBadgeProps>(), {
  offer: undefined,
  prices: () => [],
  discountText: 'Rabattiert',
  text: undefined,
  footnoteText: undefined,
  customBadges: undefined,
  className: undefined,
});

const { rebates } = storeToRefs(useOfferStore());

const discount = computed(() => {
  const prices = props.offer?.prices;
  if (!prices && !Array.isArray(prices)) return undefined;
  const defaultPrice = getPrice(prices, 'DEFAULT', 'ONETIME');
  const wasPrice = getPrice(prices, 'WAS', 'ONETIME');
  return defaultPrice?.amount && wasPrice?.amount
    ? getDiscount(defaultPrice.amount, wasPrice.amount)
    : undefined;
});

const rebate = computed(() => rebates.value[props.offer?.product?.articleNumber]);

const entries = computed(() => {
  const badges: SquareBadgeComponentProps[] = [];

  if (props.customBadges?.length > 0) {
    props.customBadges.forEach((b) => {
      badges.push(b);
    });
  }

  if (badges.length > 0 && props.showOnlyOne) return badges;

  if (rebate.value?.description) {
    badges.push({
      text: rebate.value?.description,
      footnoteText: rebate.value?.footnote,
    });
    if (props.showOnlyOne) return badges;
  }
  if (props.text) {
    badges.push({
      text: props.text,
      footnoteText: props.footnoteText,
    });
    if (props.showOnlyOne) return badges;
  }
  if (discount.value) {
    badges.push({
      text: 'Aktionspreis',
      theme: 'green',
    });
    if (props.showOnlyOne) return badges;
  }
  return badges;
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.square-badge {
  display: inline-flex;
  align-items: center;
  margin: 0 0.25rem 0.5rem;
  padding: 0.688rem 1rem;

  &__container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
  }
}
</style>
